/**
 * Google Analytics Helper (using react-ga4)
 * Utility functions untuk tracking events di Google Analytics
 */
import ReactGA from 'react-ga4';

const EVENT_PREFIX = 'explore_campaign_';

/**
 * Initialize Google Analytics with react-ga4
 * GA ID: G-71K0CDWTMJ (existing)
 * ONLY runs in PRODUCTION environment
 */
export const initGA = () => {
  if (process.env.NODE_ENV === 'production') {
    try {
      ReactGA.initialize([
        {
          trackingId: "G-71K0CDWTMJ",
        }
      ]);
      console.log('✅ Google Analytics initialized (Production Mode)');
      return true;
    } catch (error) {
      console.error('❌ GA Initialization Error:', error);
      return false;
    }
  } else {
    // Development/Local mode - GA NOT initialized
    console.log('🔧 DEVELOPMENT MODE - Google Analytics disabled');
    console.log('📊 Events will be logged to console only (not sent to GA)');
    console.log('🌍 Environment:', process.env.NODE_ENV || 'development');
    return false; // Indicate GA is not active
  }
};

/**
 * Track custom event ke Google Analytics using react-ga4
 * PRODUCTION: Send to GA4
 * DEVELOPMENT: Log to console only
 * @param {string} eventName - Nama event (e.g., 'user_login_attempt')
 * @param {object} params - Parameter tambahan untuk event
 */
export const trackEvent = (eventName, params = {}) => {
  try {
    const prefixedEventName = String(eventName || '').startsWith(EVENT_PREFIX)
      ? String(eventName || '')
      : `${EVENT_PREFIX}${String(eventName || '')}`;

    const eventData = {
      ...params,
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
    };
    
    if (process.env.NODE_ENV === 'production') {
      // PRODUCTION: Send to GA4
      ReactGA.event(prefixedEventName, eventData);
    } else {
      // DEVELOPMENT/LOCAL: Console log only (NOT sent to GA)
      console.group(`📊 GA Event: ${prefixedEventName}`);
      console.log('🏷️  Event Name:', eventName);
      console.log('🏷️  Scoped Event Name:', prefixedEventName);
      console.log('📦 Parameters:', eventData);
      console.log('⚠️  Status: NOT sent to GA (development mode)');
      console.groupEnd();
    }
  } catch (error) {
    console.error('❌ GA Tracking Error:', error);
  }
};

/**
 * Track authentication events
 */
export const trackAuth = {
  loginAttempt: (email) => trackEvent('user_login_attempt', { email }),
  loginSuccess: (userId, email) => trackEvent('user_login_success', { user_id: userId, email }),
  loginFailed: (error) => trackEvent('user_login_failed', { error_message: error }),
  registerAttempt: (email) => trackEvent('user_register_attempt', { email }),
  registerSuccess: (userId, email) => trackEvent('user_register_success', { user_id: userId, email }),
  registerFailed: (error) => trackEvent('user_register_failed', { error_message: error }),
  forgotPasswordClick: () => trackEvent('forgot_password_click'),
  logout: (userId) => trackEvent('user_logout', { user_id: userId }),
};

/**
 * Track game events
 */
export const trackGame = {
  startLoading: () => trackEvent('game_start_loading'),
  launchClick: () => trackEvent('game_launch_click'),
  loadedSuccess: (loadTime) => trackEvent('game_loaded_success', { load_time_seconds: loadTime }),
  storyStart: () => trackEvent('game_story_start'),
  prizeView: () => trackEvent('game_prize_view'),
  helpView: () => trackEvent('game_help_view'),
  hintToggle: (isShown) => trackEvent('hint_toggle', { hint_shown: isShown }),
};

/**
 * Track campus exploration events
 * PENTING: Selalu sertakan nama campus!
 */
export const trackCampus = {
  explore: (campusName, campusLocation) => trackEvent('campus_explore', { 
    campus_name: campusName,
    campus_location: campusLocation,
  }),
  programView: (campusName, campusLocation) => trackEvent('campus_program_view', { 
    campus_name: campusName,
    campus_location: campusLocation,
    content_type: 'program',
  }),
  admissionView: (campusName, campusLocation) => trackEvent('campus_admission_view', { 
    campus_name: campusName,
    campus_location: campusLocation,
    content_type: 'admission',
  }),
  virtualTourView: (campusName, campusLocation) => trackEvent('campus_virtual_tour', { 
    campus_name: campusName,
    campus_location: campusLocation,
    content_type: 'virtual_tour',
  }),
  websiteView: (campusName, campusLocation, websiteUrl) => trackEvent('campus_website_view', { 
    campus_name: campusName,
    campus_location: campusLocation,
    website_url: websiteUrl,
    content_type: 'website',
  }),
  quizAttempt: (campusName, campusLocation, userLevel, requiredLevel) => trackEvent('campus_quiz_attempt', { 
    campus_name: campusName,
    campus_location: campusLocation,
    user_level: userLevel,
    required_level: requiredLevel,
    content_type: 'quiz',
  }),
  quizLocked: (campusName, campusLocation, userLevel, requiredLevel) => trackEvent('campus_quiz_locked', { 
    campus_name: campusName,
    campus_location: campusLocation,
    user_level: userLevel,
    required_level: requiredLevel,
  }),
  triviaView: (campusName, campusLocation, triviaIndex) => trackEvent('campus_trivia_view', { 
    campus_name: campusName,
    campus_location: campusLocation,
    trivia_index: triviaIndex,
  }),
};

/**
 * Track experience & progression events
 */
export const trackExperience = {
  spotFound: (campusName, campusLocation, campusInfo, userLevel) => trackEvent('experience_spot_found', { 
    campus_name: campusName,
    campus_location: campusLocation,
    campus_info: campusInfo,
    user_level: userLevel,
  }),
  claim: (campusName, campusLocation, campusInfo, pointsEarned, newLevel, oldLevel) => trackEvent('experience_claim', { 
    campus_name: campusName,
    campus_location: campusLocation,
    campus_info: campusInfo,
    points_earned: pointsEarned,
    new_level: newLevel,
    old_level: oldLevel,
  }),
  levelUp: (newLevel, totalPoints, puzzleEarned) => trackEvent('user_level_up', { 
    new_level: newLevel,
    total_points: totalPoints,
    puzzle_earned: puzzleEarned,
  }),
  puzzlePieceEarned: (puzzleFile, currentLevel) => trackEvent('puzzle_piece_earned', { 
    puzzle_file: puzzleFile,
    current_level: currentLevel,
  }),
  xpMonitorView: (totalPoints, currentLevel, puzzleCount) => trackEvent('xp_monitor_view', { 
    total_points: totalPoints,
    current_level: currentLevel,
    puzzle_count: puzzleCount,
  }),
};

/**
 * Track puzzle & quiz events
 */
export const trackPuzzle = {
  view: (puzzleCount, totalPuzzles) => trackEvent('puzzle_view', { 
    puzzle_count: puzzleCount,
    total_puzzles: totalPuzzles,
  }),
  huntStart: (isAuthenticated) => trackEvent('puzzle_hunt_start', { 
    is_authenticated: isAuthenticated,
  }),
};

export const trackQuiz = {
  start: (campusName, campusLocation, quizType) => trackEvent('quiz_start', { 
    campus_name: campusName,
    campus_location: campusLocation,
    quiz_type: quizType,
  }),
  answerSelect: (questionId, answer, questionIndex) => trackEvent('quiz_answer_select', { 
    question_id: questionId,
    answer: answer,
    question_index: questionIndex,
  }),
  nextQuestion: (currentIndex, totalQuestions) => trackEvent('quiz_next_question', { 
    current_index: currentIndex,
    total_questions: totalQuestions,
  }),
  prevQuestion: (currentIndex, totalQuestions) => trackEvent('quiz_prev_question', { 
    current_index: currentIndex,
    total_questions: totalQuestions,
  }),
  submit: (totalAnswered, totalQuestions) => trackEvent('quiz_submit', { 
    total_answered: totalAnswered,
    total_questions: totalQuestions,
  }),
  complete: (score, campusName, campusLocation) => trackEvent('quiz_complete', { 
    score: score,
    campus_name: campusName,
    campus_location: campusLocation,
  }),
};

/**
 * Track UI interactions
 */
export const trackUI = {
  modalOpen: (modalTitle, modalType) => trackEvent('modal_open', { 
    modal_title: modalTitle,
    modal_type: modalType,
  }),
  modalClose: (modalTitle, modalType) => trackEvent('modal_close', { 
    modal_title: modalTitle,
    modal_type: modalType,
  }),
  notificationReceived: (notificationTitle, notificationType) => trackEvent('notification_received', { 
    notification_title: notificationTitle,
    notification_type: notificationType,
  }),
};

/**
 * Track page views using react-ga4
 * PRODUCTION: Send to GA4
 * DEVELOPMENT: Log to console only
 */
export const trackPageView = (pagePath, pageTitle) => {
  try {
    if (process.env.NODE_ENV === 'production') {
      // PRODUCTION: Send to GA4
      ReactGA.send({ 
        hitType: "pageview", 
        page: pagePath,
        title: pageTitle 
      });
    } else {
      // DEVELOPMENT/LOCAL: Console log only
      console.group('📊 GA Page View');
      console.log('📄 Page:', pagePath);
      console.log('🏷️  Title:', pageTitle);
      console.log('⚠️  Status: NOT sent to GA (development mode)');
      console.groupEnd();
    }
  } catch (error) {
    console.error('❌ GA Page View Error:', error);
  }
};

export default {
  initGA,
  trackEvent,
  trackAuth,
  trackGame,
  trackCampus,
  trackExperience,
  trackPuzzle,
  trackQuiz,
  trackUI,
  trackPageView,
};

